import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { GlobalContext } from "../../Utils/GlobalContext";
import "./OverviewTour.css";

// Implementação própria (sem dependência de terceiros): o pacote react-joyride
// (>=3, e também as versões 2.x republicadas) distribui apenas build ESM/CJS
// com sintaxe moderna (optional chaining, import nomeado de "Component" a
// partir de "react") incompatível com o webpack 4 usado por
// react-scripts 3.0.1 deste projeto — falha ao compilar
// ("Module parse failed: Unexpected token" / "Can't import the named export
// 'Component' from non EcmaScript module"). Sem upgrade de react-scripts
// (fora do escopo), a solução é não depender do pacote.
const OVERVIEW_STORAGE_PREFIX = "gdoc:overview";

function montarChaveStorage({ tourId, userId, entidadeId, versao }) {
  const usuario = userId || "anon";
  const entidade = entidadeId || "sem-entidade";
  return `${OVERVIEW_STORAGE_PREFIX}:${versao}:${usuario}:${entidade}:${tourId}`;
}

function calcularPosicao(alvo) {
  if (!alvo) return null;
  const rect = alvo.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
}

function OverviewTour({
  tourId,
  steps,
  versao = "v1",
  iniciarAutomaticamente = true,
  executar = true,
  onFinalizar,
}) {
  const { globalData } = useContext(GlobalContext);
  const [run, setRun] = useState(false);
  const [indice, setIndice] = useState(0);
  const [posicao, setPosicao] = useState(null);
  const tooltipRef = useRef(null);

  const userId = globalData?.user_id || localStorage.getItem("user_id");
  const entidadeId = globalData?.entidade_id || localStorage.getItem("entidade_id");

  const chaveStorage = useMemo(
    () => montarChaveStorage({ tourId, userId, entidadeId, versao }),
    [tourId, userId, entidadeId, versao]
  );

  const passos = useMemo(() => (Array.isArray(steps) ? steps : []), [steps]);

  useEffect(() => {
    if (!tourId || passos.length === 0 || !executar || !iniciarAutomaticamente) {
      setRun(false);
      return;
    }

    const jaVisto = localStorage.getItem(chaveStorage) === "S";
    setIndice(0);
    setRun(!jaVisto);
    // Marca como visto assim que o tour começa a rodar (não só ao concluir/pular):
    // se o usuário navegar para outra etapa do assistente (Anterior/Próxima) sem
    // interagir com o tour, o componente desmonta e, sem isso, o tour reapareceria
    // do zero toda vez que essa etapa fosse revisitada na mesma sessão.
    // A gravação é adiada (setTimeout) para não ser lida de volta pela segunda
    // invocação síncrona do efeito que o React.StrictMode faz em desenvolvimento
    // (monta -> desmonta -> monta) — sem o adiamento, essa segunda invocação lia
    // a marca já gravada pela primeira e cancelava o "run" antes de exibir.
    if (!jaVisto) {
      const idMarcarVisto = window.setTimeout(() => {
        localStorage.setItem(chaveStorage, "S");
      }, 0);
      return () => window.clearTimeout(idMarcarVisto);
    }
    return undefined;
  }, [tourId, passos.length, executar, iniciarAutomaticamente, chaveStorage]);

  const finalizar = (statusFinal) => {
    localStorage.setItem(chaveStorage, "S");
    setRun(false);
    if (onFinalizar) onFinalizar({ status: statusFinal });
  };

  // Recalcula (e mantém atualizada) a posição do alvo do passo atual. Fica
  // tentando indefinidamente (sem desistir/pular passo) enquanto o alvo não
  // existir no DOM — ex.: um passo aponta para dentro de um Modal que só
  // existe depois que o próprio usuário abre (não avança sozinho; a tooltip
  // some e só reaparece quando o usuário realmente abrir o modal).
  useEffect(() => {
    if (!run) return undefined;
    const passoAtual = passos[indice];
    if (!passoAtual) return undefined;

    setPosicao(null);
    let cancelado = false;
    let timeoutId = null;

    const tentar = () => {
      if (cancelado) return;
      const alvo = document.querySelector(passoAtual.target);
      if (alvo) {
        setPosicao(calcularPosicao(alvo));
        return;
      }
      timeoutId = window.setTimeout(tentar, 150);
    };
    tentar();

    const recalcular = () => {
      const alvo = document.querySelector(passoAtual.target);
      if (alvo) setPosicao(calcularPosicao(alvo));
    };
    window.addEventListener("resize", recalcular);
    window.addEventListener("scroll", recalcular, true);

    return () => {
      cancelado = true;
      if (timeoutId) window.clearTimeout(timeoutId);
      window.removeEventListener("resize", recalcular);
      window.removeEventListener("scroll", recalcular, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, indice, passos]);

  if (!tourId || passos.length === 0 || !run) return null;

  const passoAtual = passos[indice];
  const ultimo = indice === passos.length - 1;

  const irProximo = () => {
    if (ultimo) finalizar("finished");
    else setIndice((i) => i + 1);
  };
  const irAnterior = () => setIndice((i) => Math.max(0, i - 1));
  const pular = () => finalizar("skipped");

  const estiloTooltip = (() => {
    if (!posicao) return { display: "none" };
    const espacoAbaixo = window.innerHeight - (posicao.top + posicao.height);
    const abrirParaBaixo = espacoAbaixo > 180 || posicao.top < 180;
    const top = abrirParaBaixo ? posicao.top + posicao.height + 12 : posicao.top - 12;
    const left = Math.min(
      Math.max(posicao.left, 12),
      window.innerWidth - 340
    );
    return {
      top,
      left,
      transform: abrirParaBaixo ? "none" : "translateY(-100%)",
    };
  })();

  // Portal direto no <body>: renderizar dentro da árvore do assistente (ou de
  // qualquer tela com overlay/transform próprio) prende este position:fixed
  // dentro do stacking context do ancestral, ficando atrás de modais do
  // Bootstrap (que também portam para o body com z-index maior). Mesma
  // solução que o próprio react-bootstrap usa para o Modal.
  return createPortal(
    <div className="overview-tour-root">
      {posicao && <div className="overview-tour-backdrop" />}
      {posicao && (
        <div
          className="overview-tour-destaque"
          style={{
            top: posicao.top - 6,
            left: posicao.left - 6,
            width: posicao.width + 12,
            height: posicao.height + 12,
          }}
        />
      )}
      <div ref={tooltipRef} className="overview-tour-tooltip" style={estiloTooltip}>
        <div className="overview-tour-progresso">{`${indice + 1} de ${passos.length}`}</div>
        {passoAtual.title && <h4>{passoAtual.title}</h4>}
        <p>{passoAtual.content}</p>
        <div className="overview-tour-acoes">
          <button type="button" className="overview-tour-link" onClick={pular}>
            Pular
          </button>
          <div className="overview-tour-nav">
            {indice > 0 && (
              <button type="button" className="overview-tour-btn overview-tour-btn--secundario" onClick={irAnterior}>
                Anterior
              </button>
            )}
            <button type="button" className="overview-tour-btn overview-tour-btn--primario" onClick={irProximo}>
              {ultimo ? "Concluir" : "Proximo"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export { montarChaveStorage };
export default OverviewTour;
